'use strict';

/**
 * Single-flight lanes: what a queued action contends for, and when that thing is busy.
 *
 * A queued action contends for something real. A Promote contends for production. A run of the
 * plansheet-software toolchain contends for that pipeline. Nothing in the platform said so, so you
 * could queue a promote, walk away, come back, and queue it again -- and a run parked on its promote
 * gate waiting for a person to sign off did not stop a second run of the same chain from being
 * started and claimed ahead of it.
 *
 * A lane is a derived key naming the contended resource plus the set of statuses that count as still
 * in flight. Nothing is stored: both keys come off columns that already exist, so there is no column
 * to migrate and no history to backfill.
 *
 *   DeployRequest   deploy:dev, deploy:prod      Requested, Running
 *   Run             toolchain:<ToolchainKey>     Requested, Running, AwaitingConsent
 *
 * AwaitingConsent counting as open is the point of the whole module. A step waiting on a person is
 * not idle: it is holding the pipeline, and it holds the lane until the person answers.
 *
 * Deploy lanes are per environment. Two promotes cannot overlap and two dev deploys cannot overlap,
 * but a dev deploy and a promote still can: they are different environments, and treating them as
 * one lane would refuse a pair of actions that has never actually collided.
 *
 * The two lanes layer rather than duplicate. The run lane stops a second run of one chain; the
 * deploy lane stops a second promote no matter who asked for it, a person on the Releases page or a
 * node walking a toolchain. Neither is a substitute for the other.
 *
 * This file is the shared half: the derivation and the words, with no database in it, so the server
 * guard and the pages that grey out a button agree on what busy means and say it the same way. The
 * server half (the reads) is source/server/queue/SingleFlight.js. Sharing this direction is the
 * convention already in the tree: Board-Endpoint and Review-Endpoint both reach down into
 * EntityDescriptors.js rather than keeping a second copy of what an entity is.
 */

const _OPEN_DEPLOY_STATUSES = ['Requested', 'Running'];
const _OPEN_RUN_STATUSES = ['Requested', 'Running', 'AwaitingConsent'];

/**
 * Which environment a deploy contends for. Environment is the honest answer because it names the
 * resource; Action is the fallback for a row that did not carry one, which is how the node's
 * executor can post when a toolchain step leaves Environment off its Args.
 */
function deployLaneKey(pRow)
{
	let tmpRow = pRow || {};
	let tmpEnvironment = String(tmpRow.Environment || '').trim().toLowerCase();
	if (!tmpEnvironment)
	{
		tmpEnvironment = (String(tmpRow.Action || '') === 'Promote') ? 'prod' : 'dev';
	}
	return 'deploy:' + tmpEnvironment;
}

/**
 * Which pipeline a run contends for. A deploy chain contends per toolchain: one deploy-dev run, one
 * plansheet-software run. A work-item build is different -- it contends for the WORK ITEM, not the
 * chain, because many items build the same work-item toolchain at once and must not queue behind each
 * other. So a work-item run (a work-item toolchain carrying an IDWorkItem) gets a per-item lane: two
 * runs of the same item cannot overlap (a stranded one blocks only its own item), but two different
 * items run side by side.
 */
function runLaneKey(pRow)
{
	let tmpRow = pRow || {};
	let tmpKey = String(tmpRow.ToolchainKey || '').trim();
	let tmpIDWorkItem = Number(tmpRow.IDWorkItem) || 0;
	if (tmpIDWorkItem && tmpKey.indexOf('work-item') === 0) { return 'workitem:' + tmpIDWorkItem; }
	return 'toolchain:' + tmpKey;
}

function isOpenDeploy(pRow)
{
	return _OPEN_DEPLOY_STATUSES.indexOf(String((pRow || {}).Status || '')) >= 0;
}

function isOpenRun(pRow)
{
	return _OPEN_RUN_STATUSES.indexOf(String((pRow || {}).Status || '')) >= 0;
}

/**
 * The open row holding pLaneKey, given rows already in hand. The server reads them out of the
 * database and the pages already have them loaded, so both go through here.
 *
 * Oldest first among the open ones, so the sentence names the request that has been holding the lane
 * rather than the most recent thing to pile up behind it.
 */
function openestOf(pRows, pLaneKey, fLaneKey, fIsOpen)
{
	let tmpOpen = (pRows || []).filter((pRow) => fIsOpen(pRow) && fLaneKey(pRow) === pLaneKey);
	if (!tmpOpen.length) { return null; }
	return tmpOpen.reduce((pOldest, pRow) => (_sortValue(pRow) < _sortValue(pOldest)) ? pRow : pOldest, tmpOpen[0]);
}

function _sortValue(pRow)
{
	let tmpRow = pRow || {};
	return Number(tmpRow.IDDeployRequest || tmpRow.IDRun || 0) || 0;
}

function findOpenDeployIn(pRows, pLaneKey)
{
	return openestOf(pRows, pLaneKey, deployLaneKey, isOpenDeploy);
}

function findOpenRunIn(pRows, pLaneKey)
{
	return openestOf(pRows, pLaneKey, runLaneKey, isOpenRun);
}

/**
 * What the thing in the lane is, in words. "Dev deploy" and "promotion to production" rather than
 * DeployDev and Promote, because this sentence is read by a person on a page, not by a machine.
 */
function _deployNoun(pRow)
{
	return (String((pRow || {}).Action || '') === 'Promote') ? 'A promotion to production' : 'A dev deploy';
}

function statusWords(pStatus)
{
	let tmpMap =
	{
		Requested: 'queued',
		Running: 'running',
		AwaitingConsent: 'waiting for someone to approve it'
	};
	return tmpMap[String(pStatus || '')] || String(pStatus || '').toLowerCase();
}

/**
 * The one sentence the 409 carries and the page shows, so a person reads the same words whichever
 * way they hit the lane. It says what is in flight, which record it is, and where it got to.
 */
function describeDeploy(pRow)
{
	let tmpRow = pRow || {};
	let tmpNumber = Number(tmpRow.DeployRequestNumber) || Number(tmpRow.Number) || Number(tmpRow.IDDeployRequest) || 0;
	return _deployNoun(tmpRow) + ' is already in flight (request #' + tmpNumber + ', '
		+ statusWords(tmpRow.Status) + '). Wait for it to finish, or cancel it on the Releases page.';
}

function describeRun(pRow)
{
	let tmpRow = pRow || {};
	let tmpNumber = Number(tmpRow.RunNumber) || Number(tmpRow.Number) || Number(tmpRow.IDRun) || 0;
	return 'A run of ' + (tmpRow.ToolchainKey || 'that toolchain') + ' is already in flight (run #'
		+ tmpNumber + ', ' + statusWords(tmpRow.Status) + ').';
}

module.exports =
{
	deployLaneKey: deployLaneKey,
	runLaneKey: runLaneKey,
	isOpenDeploy: isOpenDeploy,
	isOpenRun: isOpenRun,
	findOpenDeployIn: findOpenDeployIn,
	findOpenRunIn: findOpenRunIn,
	describeDeploy: describeDeploy,
	describeRun: describeRun,
	statusWords: statusWords,
	OPEN_DEPLOY_STATUSES: _OPEN_DEPLOY_STATUSES,
	OPEN_RUN_STATUSES: _OPEN_RUN_STATUSES
};
